import type { ReactElement } from "react";
import * as Sentry from "@sentry/browser";
import { z } from "zod";

import { RouterLink } from "@js/components/link";
import { LocalStorage, SessionStorage } from "@js/services";
import type { User } from "@js/types/auth";

import { PROF_NET_ONBOARDING_PATHS, SIGN_UP_LINK } from "./constants";

export const sentrySetUser = (user: User) =>
  Sentry.getCurrentScope().setUser({
    id: String(user.id),
    email: user.email,
    name: user.public_name,
    accountType: user.account_type,
    approved:
      user.account_type === ENUMS.AccountType.FREELANCER
        ? user.freelancer_approved
        : undefined,
    backendRevision: SETTINGS.BACKEND_REVISION,
    isImpersonatedSession: user.is_impersonated_session,
  });

export const getUserBrowserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const mapMessageCodeToUserMessage = (
  message: string | undefined,
): string | null => {
  switch (message) {
    case "session_expired":
      return "Session expired. Please sign in again.";

    case "password_reset":
      return "Password has been reset. Please sign in with the new password.";

    case "from_password_change":
      return "Password successfully changed. Please sign in again.";

    case ENUMS.AuthFailedErrorCodes.EXTRA_AUTH_REQUIRED as string:
      return "To finish connecting with LinkedIn please sign in using your existing credentials.";

    case "user_logged_out":
      return "You were logged out. Please sign in again to continue using the site.";

    case undefined:
    default:
      return null;
  }
};

export const mapErrorCodeToUserError = (
  code: EnumType<typeof ENUMS.AuthFailedErrorCodes> | string | undefined,
): ReactElement | string | null => {
  switch (code) {
    case ENUMS.AuthFailedErrorCodes.NO_ACCOUNT:
      return (
        <>
          <p className="mt0">Account not found.</p>
          <p>
            <span className="black">
              Please{" "}
              <RouterLink to={SIGN_UP_LINK}>
                create one with your email.
              </RouterLink>
            </span>
          </p>
        </>
      );

    case ENUMS.AuthFailedErrorCodes.AUTH_CANCELLED:
      return "Authentication cancelled.";

    case ENUMS.AuthFailedErrorCodes.ACCOUNT_ALREADY_EXISTS:
      return "Account already exists.";

    case ENUMS.AuthFailedErrorCodes.NO_SOCIAL_ACCOUNT:
      return (
        "Your account already exists. Please sign in with email or sign up with " +
        "LinkedIn to connect it to your account"
      );

    case ENUMS.AuthFailedErrorCodes.ACCOUNT_DISABLED:
      return "Your account is disabled. Please email activate@usebraintrust.com with your account email to re-enable it.";

    case ENUMS.AuthFailedErrorCodes.LAST_NAME_HIDDEN:
      return "We can't read your last name from LinkedIn - it's probably hidden. Please change your privacy settings in order to use LinkedIn sign up.";

    case ENUMS.ConfirmAction.DELETE_ACCOUNT:
      return "You have to be logged in to continue deleting your account.";

    case "confirm_action":
      return "You have to be logged in to confirm that action.";

    case undefined:
    default:
      return null;
  }
};

export const mapEmailErrorCode = (error: string, isSignup?: boolean) => {
  switch (error) {
    case ENUMS.EmailValidationError.INVALID:
      return "Invalid email format.";
    case ENUMS.EmailValidationError.REQUIRED:
      return "Email is required.";
    case ENUMS.EmailValidationError.RESTRICTED:
      return isSignup
        ? "Signup could not be processed at this time."
        : "Change could not be processed at this time.";
    case ENUMS.EmailValidationError.MAX_LENGTH:
      return "Sorry, the email address you entered is too long. Try to enter a shorter address.";
    default:
      return error || null;
  }
};

// synchronize login between tabs
const syncLoginCallback = (event: StorageEvent) => {
  if (event.key === LocalStorage.keys.LOGGED_IN && event.newValue === "true") {
    location.reload();
  }
};

export const syncLoginWatchLocalStorage = () => {
  window.addEventListener("storage", syncLoginCallback);
};

export const syncLoginRemoveWatcher = () => {
  window.removeEventListener("storage", syncLoginCallback);
};

export const syncLoginSaveToLocalStorage = () => {
  LocalStorage.setItem(LocalStorage.keys.LOGGED_IN, "true");
};

export const setFreelancerTimezoneUpdated = () => {
  SessionStorage.setItem(
    SessionStorage.keys.FREELANCER_TIMEZONE_UPDATED,
    "true",
  );
};

export const getIsFreelancerTimezoneUpdated = () => {
  return Boolean(
    SessionStorage.getItem(SessionStorage.keys.FREELANCER_TIMEZONE_UPDATED),
  );
};

export const LOGIN_FLOW_PARAM = "login_flow";

export const LOGIN_FLOWS = {
  POST_PUBLIC_APPLICATION: "post_public_application",
} as const;

export const isPostPublicApplicationLoginFlow = (params: URLSearchParams) => {
  const flowParam = params.get(LOGIN_FLOW_PARAM);
  return flowParam === LOGIN_FLOWS.POST_PUBLIC_APPLICATION;
};

export const EMPLOYER_REGISTER_INVITATION_PARAMS = {
  INVITATION_KEY: "invitation_key",
  NEW_EMPLOYER_INVITATION_KEY: "new_employer_invitation_key",
} as const;

export const FREELANCER_REGISTER_INVITATION_PARAMS = {
  FREELANCER_INVITATION_KEY: "freelancer_invitation_key",
} as const;

export const REGISTER_INVITATION_PARAMS = {
  ...EMPLOYER_REGISTER_INVITATION_PARAMS,
  ...FREELANCER_REGISTER_INVITATION_PARAMS,
} as const;

export const REGISTER_FLOW_PARAM = "register_flow";
export const REGISTER_FLOWS = {
  POST_PUBLIC_APPLICATION: "post_public_application",
  CLIENT_INVITATION: "client_invitation",
} as const;

export const registerFormPrefillSchema = z.object({
  first_name: z.string(),
  last_name: z.string(),
  email: z.string().email(),
});

export type RegisterFormPrefill = z.infer<typeof registerFormPrefillSchema>;

export const REGISTER_PREFILL_PARAMS: Record<
  string,
  keyof RegisterFormPrefill
> = {
  FIRST_NAME: "first_name",
  LAST_NAME: "last_name",
  EMAIL: "email",
};

export const registerPrefillParams = Object.values(REGISTER_PREFILL_PARAMS);

const employerInviationParamsList = Object.values(
  EMPLOYER_REGISTER_INVITATION_PARAMS,
);

export const isEmployerRegisterInvitation = (
  params: URLSearchParams,
): boolean => {
  return employerInviationParamsList.some((param) => params.has(param));
};

const freelancerInviationParamsList = Object.values(
  FREELANCER_REGISTER_INVITATION_PARAMS,
);

export const isFreelancerRegisterInvitation = (
  params: URLSearchParams,
): boolean => {
  return freelancerInviationParamsList.some((param) => params.has(param));
};

export const isRegisterFormPrefill = (params: URLSearchParams): boolean => {
  const parseResult = registerFormPrefillSchema.safeParse(
    Object.fromEntries(params.entries()),
  );

  return parseResult.success;
};

export const isPostPublicApplicationRegisterFlow = (
  params: URLSearchParams,
) => {
  const flowParam = params.get(REGISTER_FLOW_PARAM);
  return flowParam === REGISTER_FLOWS.POST_PUBLIC_APPLICATION;
};

export const isClientInvitationSignupFlow = (
  params: URLSearchParams,
): boolean => {
  // Backend redirects directly to /sign_up/create_account?param...
  // .. so we can't set the proper register flow param during the index redirect
  const newEmployerInvitationParam = params.get(
    EMPLOYER_REGISTER_INVITATION_PARAMS.NEW_EMPLOYER_INVITATION_KEY,
  );
  if (newEmployerInvitationParam) {
    return true;
  }

  const flowParam = params.get(REGISTER_FLOW_PARAM);
  return flowParam === REGISTER_FLOWS.CLIENT_INVITATION;
};

export const isPostPublicApplicationFlowWithPrefill = (
  params: URLSearchParams,
): boolean => {
  if (!isPostPublicApplicationRegisterFlow(params)) {
    return false;
  }

  const parseResult = registerFormPrefillSchema.safeParse(
    Object.fromEntries(params.entries()),
  );

  return parseResult.success;
};

export const getSignupRouteRedirect = (params: URLSearchParams) => {
  const paramsCopy = new URLSearchParams(params);
  if (isEmployerRegisterInvitation(paramsCopy)) {
    paramsCopy.set(REGISTER_FLOW_PARAM, REGISTER_FLOWS.CLIENT_INVITATION);
    return `${PROF_NET_ONBOARDING_PATHS.CREATE_ACCOUNT_PAGE.path}?${paramsCopy.toString()}`;
  }

  if (
    isFreelancerRegisterInvitation(paramsCopy) ||
    isPostPublicApplicationFlowWithPrefill(paramsCopy)
  ) {
    paramsCopy.set(REGISTER_FLOW_PARAM, REGISTER_FLOWS.POST_PUBLIC_APPLICATION);
    return `${PROF_NET_ONBOARDING_PATHS.CREATE_ACCOUNT_PAGE.path}?${paramsCopy.toString()}`;
  }

  return `${PROF_NET_ONBOARDING_PATHS.CREATE_ACCOUNT_PAGE.path}?${paramsCopy.toString()}`;
};
